import React, { useMemo, useState } from "react";
import { useVideosWithEntities } from "../../hooks/useVideosWithEntities";
import { migrateLegacyData, MigrationReport } from "../../lib/videoAdmin";
import { notify } from "../../utils/notify";
import { primaryButton, Section } from "../admin/ui";

export default function MaintenanceView() {
  const { videos, companies, agencies, refetch } = useVideosWithEntities("admin");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<MigrationReport | null>(null);

  const stats = useMemo(() => {
    const withoutStatus = videos.filter((v) => !v.status).length;
    const withoutKey = videos.filter((v) => !v.sourceKey).length;
    const orphanBrand = videos.filter((v) => !v.videoFor?.id).length;
    const unclassified = videos.filter((v) => !v.industries.length && !v.formats.length && !v.styles.length).length;
    const noThumb = videos.filter((v) => !v.thumbnailURL).length;
    return { withoutStatus, withoutKey, orphanBrand, unclassified, noThumb };
  }, [videos]);

  const run = async () => {
    setRunning(true);
    try {
      const result = await migrateLegacyData();
      setReport(result);
      refetch();
      notify({ isSuccess: true, message: "Migration terminée" });
    } catch (err) {
      notify({ isSuccess: false, message: "La migration a échoué", info: err instanceof Error ? err.message : undefined });
    } finally {
      setRunning(false);
    }
  };

  const rows: [string, number, string][] = [
    ["Vidéos sans statut", stats.withoutStatus, "Invisibles sur le site une fois les nouvelles règles de sécurité déployées."],
    ["Vidéos sans clé de doublon", stats.withoutKey, "Nécessaire pour détecter les doublons dans Quick Add."],
    ["Vidéos sans marque valide", stats.orphanBrand, "Marque supprimée ou jamais renseignée."],
    ["Vidéos non classées", stats.unclassified, "Aucune industrie, aucun format, aucun style."],
    ["Vidéos sans miniature enregistrée", stats.noThumb, "Le site retombe sur la miniature YouTube quand c'est possible."],
  ];

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <Section
        title="Migration des anciennes données"
        description="À lancer une fois depuis la version locale (npm start), avant de déployer les règles de sécurité, les fonctions et le site : sinon le site public n'affiche aucune vidéo. Sans risque de la relancer : les fiches déjà migrées ne sont pas modifiées."
        actions={
          <button className={primaryButton} onClick={run} disabled={running || (stats.withoutStatus === 0 && stats.withoutKey === 0)}>
            {running ? "Migration…" : stats.withoutStatus || stats.withoutKey ? "Lancer la migration" : "Données à jour"}
          </button>
        }
      >
        <p className="text-sm text-neutral-600">
          Les vidéos sans statut passent en « publiée » (elles sont déjà publiques aujourd'hui), reçoivent
          une clé de doublon, et les logos enregistrés en texte sont convertis en liste.
        </p>
        {report && (
          <p className="text-sm bg-emerald-50 text-emerald-900 rounded-md px-3 py-2">
            {report.videosPublished} vidéo(s) passée(s) en publiée, {report.videosKeyed} clé(s) ajoutée(s),{" "}
            {report.entitiesNormalised} logo(s) normalisé(s).
          </p>
        )}
      </Section>

      <Section title="Santé du catalogue" description={`${videos.length} vidéos · ${companies.length} marques · ${agencies.length} studios`}>
        <dl className="divide-y divide-neutral-100">
          {rows.map(([label, value, help]) => (
            <div key={label} className="py-2.5 grid grid-cols-[1fr_auto] gap-x-4">
              <dt className="text-sm text-neutral-800">{label}</dt>
              <dd className={`text-sm tabular-nums font-medium ${value ? "text-amber-700" : "text-emerald-700"}`}>{value}</dd>
              <p className="text-xs text-neutral-500 col-span-2">{help}</p>
            </div>
          ))}
        </dl>
      </Section>
    </div>
  );
}
